/**
 * @file: light.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */
import { PresentationTheme } from '@euler/app/tv/theme/types';

const theme: PresentationTheme = {
  page: {
    bg: 'rgba(242, 242, 242, 1)',
  },
  contentLoader: {
    fg: '#f7f7f7',
    bg: '#e7e7e7',
  },
  panel: {
    bg: 'rgba(255, 255, 255, 1)',
    titleTextColor: 'rgba(0, 0, 0, 0.6)',
    separatorStartColor: 'rgba(0, 0, 0, 0.4)',
    separatorEndColor: 'rgba(0, 0, 0, 0.1)',
    infoLabelColor: 'rgba(0, 0, 0, 0.5)',
    infoValueColor: 'rgba(0, 0, 0, 0.9)',
    listItemSeparatorColor: 'rgba(0, 0, 0, 0.1)',
    headerLinkTextColor: 'rgba(48, 117, 221, 1)',
  },
  statusBadge: {
    preInspection: {
      pending: 'rgba(239, 151, 19, 1)',
      inprogress: 'rgba(48, 117, 221, 1)',
      finished: 'rgba(25, 202, 181, 1)',
    },
    inspection: {
      pending: 'rgba(239, 151, 19, 1)',
      inprogress: 'rgba(48, 117, 221, 1)',
      finished: 'rgba(25, 202, 181, 1)',
    },
    construction: {
      pending: 'rgba(224, 105, 38, 1)',
      inprogress: 'rgba(48, 117, 221, 1)',
      finished: 'rgba(25, 202, 181, 1)',
    },
    deliveryCheck: {
      pending: 'rgba(64, 195, 100, 1)',
      inprogress: 'rgba(48, 117, 221, 1)',
      finished: 'rgba(25, 202, 181, 1)',
    },
    task: {
      pending: 'rgba(64, 195, 100, 1)',
      inprogress: 'rgba(48, 117, 221, 1)',
      finished: 'rgba(25, 202, 181, 1)',
      canceled: 'rgba(125, 138, 146, 1)',
    },
  },
  emptyPlaceholder: {
    textColor: 'rgba(0, 0, 0, 0.5)',
    iconColor: 'rgba(0, 0, 0, 0.5)',
    errorTextColor: 'rgba(0, 0, 0, 0.5)',
    iconImage: require('@euler/assets/img/empty-placeholder-light.png'),
  },
  memberInfo: {
    primaryBgColor: 'rgba(255, 255, 255, 0.9)',
    secondaryBgColor: 'rgba(48, 117, 221, 1)',
    nameTextColor: 'rgba(255, 255, 255, 1)',
    jobTitleTextColor: 'rgba(255, 255, 255, 0.3)',
    detail: {
      bodyTextColor: 'rgba(0, 0, 0, 0.6)',
      quoteBgColor: 'rgba(125, 138, 146, 1)',
      quoteBorderColor: 'rgba(0, 0, 0, 0.3)',
    },
  },
  lpn: {
    input: {
      borderColor: 'rgba(0, 0, 0, 0.2)',
      activeBorderColor: 'rgba(48, 117, 221, 1)',
      textColor: 'rgba(0, 0, 0, 0.8)',
    },
    keyboard: {
      btnBgColor: 'rgba(0, 0, 0, 0.07)',
      btnTextColor: 'rgba(0, 0, 0, 0.8)',
      doneBtnBgColor: 'rgba(48, 117, 221, 1)',
    },
  },
  search: {
    criterion: {
      backButtonTextColor: 'rgba(0, 0, 0, 0.5)',
      optionLabelTextColor: 'rgba(0, 0, 0, 0.4)',
      optionValueTextColor: 'rgba(0, 0, 0, 0.6)',
      optionBorderColor: 'rgba(0, 0, 0, 0.2)',
      optionArrowColor: 'rgba(0, 0, 0, 0.8)',
      optionListItem: {
        titleTextColor: 'rgba(0, 0, 0, 0.9)',
        checkmarkIconColor: 'rgba(0, 0, 0, 0.5)',
        separatorColor: 'rgba(0, 0, 0, 0.1)',
      },
    },
    bookmark: {
      iconColor: 'rgba(239, 151, 19, 1)',
      titleTextColor: 'rgba(0, 0, 0, 0.9)',
      tagBorderColor: 'rgba(0, 0, 0, 0.2)',
      tagTextColor: 'rgba(0, 0, 0, 0.5)',
      separatorLineColor: 'rgba(0, 0, 0, 0.1)',
    },
    separatorLineColor: 'rgba(0, 0, 0, 0.1)',
  },

  modal: {
    bgColor: 'rgba(255, 255, 255, 0.9)',
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },

  toast: {
    bgColor: '#222222',
    iconColor: '#ffffff',
    textColor: '#ffffff',
  },

  alert: {
    bgColor: 'rgba(255, 255, 255, 1)',
    backdropColor: 'rgba(0, 0, 0, 0.5)',
    titleTextColor: 'rgba(0, 0, 0, 0.9)',
    messageTextColor: 'rgba(0, 0, 0, 0.5)',
    defaultButtonTextColor: 'rgba(0, 0, 0, 0.9)',
    textInputBgColor: 'rgba(255, 255, 255, 1)',
    textInputBorderColor: 'rgba(0, 0, 0, 0.3)',
    textInputTextColor: 'rgba(0, 0, 0, 0.9)',
    textInputPlaceholderTextColor: 'rgba(0, 0, 0, 0.5)',
    errorTextColor: 'rgba(246, 60, 104, 1)',
    buttons: {
      default: {
        borderColor: 'rgba(0, 0, 0, 0.4)',
        textColor: 'rgba(0, 0, 0, 0.9)',
      },
      destructive: {
        textColor: 'rgba(246, 60, 104, 1)',
        borderColor: 'rgba(246, 60, 104, 1)',
      },
      cancel: {
        borderColor: 'rgba(0, 0, 0, 0.4)',
        textColor: 'rgba(0, 0, 0, 0.9)',
      },
      confirm: {
        bgColor: ['rgba(58, 226, 206, 1)', 'rgba(48, 121, 231, 1)'],
        textColor: 'rgba(255, 255, 255, 0.9)',
      },
    },
  },

  button: {
    startColor: 'rgba(58, 226, 206, 1)',
    endColor: 'rgba(48, 121, 231, 1)',
    textColor: 'rgba(255, 255, 255, 0.9)',
  },

  launchScreen: {
    bgColor: 'rgba(255, 255, 255, 1)',
    brandTitleTextColor: '#000',
    footerTextColor: 'rgba(0, 0, 0, 0.8)',
    footerCopyrightTextColor: 'rgba(0, 0, 0, 0.5)',
    footerPoweredByTextColor: 'rgba(0, 0, 0, 0.35)',
    activityIndicatorColor: 'rgba(0, 0, 0, 0.2)',
  },

  navbar: {
    backButtonColor: 'rgba(0, 0, 0, 0.8)',
    titleTextColor: 'rgba(0, 0, 0, 0.8)',
    action: {
      bgColor: 'rgba(0, 0, 0, 0.03)',
      iconColor: 'rgba(0, 0, 0, 0.5)',
      textColor: 'rgba(0, 0, 0, 0.6)',
    },
  },

  auth: {
    mainPanelBgColor: '#eee',
    sidePanelBgColor: '#009FD4',
    sidePanelVideoBgColor: '#000000',
    sideTitleTextColor: 'rgba(255, 255, 255, 1)',
    sideSubTitleTextColor: 'rgba(255, 255, 255, 0.7)',
    loginHeaderTitleTextColor: '#009FD4',
    loginHeaderSubTitleTextColor: 'rgba(0, 0, 0, 0.5)',
    linkTextColor: '#009FD4',
    buttonBgColor: '#009FD4',
    buttonTextColor: '#fff',
    disabledButtonBgColor: '#CED1D9',
    disabledButtonTextColor: 'rgba(0, 0, 0, 0.15)',
    agreementLabelTextColor: 'rgba(0, 0, 0, 0.5)',
    agreementCheckBoxFillColor: '#009FD4',
    backButtonColor: '#9498A2',
    avatarBgColor: '#fff',
    codeFieldBgColor: '#fff',
    codeFieldTextColor: '#009FD4',
    codeTipTextColor: '#4A4A4A',
    codeTtlTextColor: '#9498A2',
    textInput: {
      bgColor: '#fff',
      textColor: '#000',
      placeholderTextColor: 'rgba(0, 0, 0, 0.5)',
      tvFocusColor: '#ddd',
    },
    loginMethods: {
      titleTextColor: '#9498A2',
      separatorLineColor: '#CED1D9',
      iconBgColor: '#fff',
      iconColor: '#000000',
    },
  },

  settings: {
    versionTextColor: 'rgba(0, 0, 0, 0.35)',
    userInfo: {
      titleColor: 'rgba(0, 0, 0, 0.8)',
      roleBorderColor: 'rgba(0, 0, 0, 0.2)',
      roleTextColor: 'rgba(0, 0, 0, 0.6)',
    },
    appearance: {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
      borderColor: 'rgba(255, 255, 255, 0.2)',
      darkModeColor: 'rgba(0, 0, 0, 0.6)',
      lightModeColor: 'rgba(239, 151, 19, 1)',
    },
    cell: {
      iconColor: 'rgba(0, 0, 0, 0.9)',
      textColor: 'rgba(0, 0, 0, 0.6)',
      borderColor: 'rgba(0, 0, 0, 0.2)',
    },
  },
  dashboard: {
    navbar: {
      bg: 'rgba(48, 117, 221, 1)',
      storeName: {
        color: 'rgba(255, 255, 255, 0.8)',
        icon: {
          color: 'rgba(25, 202, 181, 1)',
        },
      },
      searchInput: {
        textColor: 'rgba(255, 255, 255, 1)',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        placeholderTextColor: 'rgba(255, 255, 255, 1)',
        icon: { color: 'rgba(255, 255, 255, 0.6)' },
      },
      icon: {
        inactiveColor: 'rgba(255, 255, 255, 0.4)',
        activeStartColor: 'rgba(81, 235, 207, 1)',
        activeEndColor: 'rgba(221, 239, 255, 1)',
      },
      exitPresentationMode: {
        textColor: 'rgba(0, 0, 0, 0.9)',
        borderColor: 'rgba(0, 0, 0, 0.5)',
      },
    },
    tab: {
      textColor: 'rgba(0, 0, 0, 0.6)',
      activeTextColor: 'rgba(17, 17, 17, 1)',
      activeEffect: {
        startColor: 'rgba(48, 173, 243, 1)',
        endColor: 'rgba(255, 255, 255, 0)',
      },
    },
    mode: {
      iconColor: 'rgba(0, 0, 0, 0.2)',
      activeIconColor: 'rgba(0, 0, 0, 0.8)',
    },
    card: {
      bg: 'rgba(255, 255, 255, 1)',
      licensePlateNoTextColor: 'rgba(34, 34, 34, 1)',
      vehicleNameTextColor: 'rgba(34, 34, 34, 1)',
      mileageTextColor: 'rgba(0, 0, 0, 0.8)',
      mileageBorderColor: 'rgba(0, 0, 0, 0.2)',
      separatorColor: 'rgba(0, 0, 0, 0.1)',
      labelColor: 'rgba(0, 0, 0, 0.4)',
      valueColor: 'rgba(51, 51, 51, 1)',
      activityIndicatorColor: 'rgba(0, 0, 0, 0.15)',
      observeStatus: {
        observedTextColor: 'rgba(64, 195, 100, 1)',
        unobservedTextColor: 'rgba(51, 51, 51, 1)',
        observedBorderColor: 'rgba(64, 195, 100, 1)',
        unobservedBorderColor: 'rgba(51, 51, 51, 1)',
      },
      icons: {
        serviceAgent: {
          color: 'rgba(0, 0, 0, 0.6)',
          startColor: 'rgba(253, 142, 106, 1)',
          endColor: 'rgba(128, 77, 235, 1)',
        },
        startTime: {
          color: 'rgba(255, 255, 255, 0.6)',
          startColor: 'rgba(91, 189, 243, 1)',
          endColor: 'rgba(102, 79, 241, 1)',
        },
        endTime: {
          color: 'rgba(255, 255, 255, 0.6)',
          startColor: 'rgba(246, 121, 67, 1)',
          endColor: 'rgba(246, 221, 89, 1)',
        },
        timeElapsed: {
          color: 'rgba(0, 0, 0, 0.6)',
          startColor: 'rgba(103, 237, 140, 1)',
          endColor: 'rgba(79, 169, 234, 1)',
        },
      },
    },
    contentLoader: {
      fg: '#f7f7f7',
      bg: '#e7e7e7',
    },
  },
  task: {
    navbar: {
      backButtonColor: 'rgba(0, 0, 0, 0.8)',
      licensePlateNoTextColor: 'rgba(0, 0, 0, 0.8)',
      action: {
        bgColor: 'rgba(0, 0, 0, 0.03)',
        iconColor: 'rgba(0, 0, 0, 0.5)',
        textColor: 'rgba(0, 0, 0, 0.6)',
      },
    },
    bottomBar: {
      bg: 'rgba(48, 117, 221, 1)',
      textColor: 'rgba(255, 255, 255, 0.6)',
      activeTextColor: 'rgba(255, 255, 255, 1)',
    },
    media: {
      titleColor: 'rgba(0, 0, 0, 0.6)',
      subTitleColor: 'rgba(0, 0, 0, 0.3)',
    },
    score: {
      labelTextColor: 'rgba(0, 0, 0, 0.8)',
      subLabelTextColor: 'rgba(0, 0, 0, 0.3)',
      outerArcColor: 'rgba(0, 0, 0, 0.1)',
      innerArcBgColor: 'rgba(0, 0, 0, 0.1)',
      innerArcColorStops: [
        [0, 'rgba(246, 60, 104, 1)'],
        [0.5, '#FF9900'],
        [0.8, '#F2D671'],
        [1, '#40C779'],
      ],
    },
    defectiveBadge: {
      textColor: 'rgba(255, 255, 255, 1)',
      bgColor: {
        urgent: 'rgba(244, 79, 99, 1)',
        warning: 'rgba(255, 138, 0, 1)',
        defective: 'rgba(254, 182, 74, 1)',
        normal: 'rgba(25, 202, 181, 1)',
      },
    },
    scrollRow: {
      separatorStartColor: 'rgba(0, 0, 0, 0.15)',
      separatorEndColor: 'rgba(0, 0, 0, 0.01)',
      head: {
        bg: 'rgba(232, 233, 235, 1)',
        titleTextColor: 'rgba(0, 0, 0, 0.8)',
        subTitleTextColor: 'rgba(0, 0, 0, 0.6)',
        labelTextColor: 'rgba(0, 0, 0, 0.6)',
        valueTextColor: 'rgba(0, 0, 0, 0.9)',
        statusTextColor: 'rgba(255, 255, 255, 1)',
      },
      user: {
        labelTextColor: 'rgba(0, 0, 0, 0.6)',
        valueTextColor: 'rgba(0, 0, 0, 0.9)',
        detailLinkTextColor: 'rgba(0, 0, 0, 0.3)',
      },
      media: {
        titleColor: 'rgba(0, 0, 0, 0.6)',
        subTitleColor: 'rgba(0, 0, 0, 0.3)',
      },
    },
    detail: {
      vehicleBanner: {
        vehicleNameTextColor: 'rgba(221, 221, 221, 1)',
        startColor: 'rgba(48, 117, 221, 1)',
        endColor: 'rgba(77, 208, 237, 1)',
        infoLabelColor: 'rgba(255, 255, 255, 0.6)',
        infoValueColor: 'rgba(255, 255, 255, 1)',
        separatorStartColor: 'rgba(255, 255, 255, 1)',
        separatorEndColor: 'rgba(255, 255, 255, 0.1)',
        observedStatusColor: 'rgba(255, 255, 255, 0.6)',
        unobservedStatusColor: 'rgba(255, 255, 255, 0.1)',
        starIconColor: 'rgba(239, 151, 19, 1)',
        outlineStarIconColor: 'rgba(0, 0, 0, 0.5)',
      },
      card: {
        bg: 'rgba(255, 255, 255, 1)',
        titleTextColor: 'rgba(0, 0, 0, 0.9)',
        titleSeparatorColor: 'rgba(0, 0, 0, 0.1)',
        infoLabelColor: 'rgba(0, 0, 0, 0.6)',
        infoValueColor: 'rgba(0, 0, 0, 0.9)',
        status: {
          bg: {
            pending: 'rgba(0, 0, 0, 0.2)',
            inprogress: 'rgba(48, 117, 221, 1)',
            finished: 'rgba(25, 202, 181, 1)',
          },
          fg: {
            pending: 'rgba(255, 255, 255, 1)',
            inprogress: 'rgba(255, 255, 255, 1)',
            finished: 'rgba(255, 255, 255, 1)',
          },
        },
      },
    },
    preInspection: {
      troubleLight: {
        textColor: 'rgba(0, 0, 0, 0.75)',
        badge: {
          borderColor: 'rgba(0, 0, 0, 0.2)',
          bgColor: 'transparent',
          textColor: 'rgba(0, 0, 0, 0.5)',
          activeBorderColor: 'rgba(0, 0, 0, 0.2)',
          activeBgColor: 'rgba(25, 202, 181, 1)',
          activeTextColor: 'rgba(255, 255, 255, 1)',
        },
      },
    },
    inspection: {
      techInfo: {
        barColor: 'rgba(0, 0, 0, 0.5)',
        nameLabelTextColor: 'rgba(0, 0, 0, 1)',
        nameTextColor: 'rgba(0, 0, 0, 1)',
        timeTextColor: 'rgba(0, 0, 0, 0.6)',
        detailLinkTextColor: 'rgba(0, 0, 0, 0.6)',
      },
      defectiveChart: {
        labelColor: 'rgba(0, 0, 0, 0.6)',
        disabledLabelColor: 'rgba(0, 0, 0, 0.05)',
        iconColor: 'rgba(0, 0, 0, 0.6)',
        disabledIconColor: 'rgba(0, 0, 0, 0.05)',
        counterColor: 'rgba(0, 0, 0, 0.8)',
        disabledCounterColor: 'rgba(0, 0, 0, 0.05)',
        circleBgColor: 'rgba(0, 0, 0, 0.1)',
        defectiveColor: {
          urgent: 'rgba(244, 79, 99, 1)',
          warning: 'rgba(255, 138, 0, 1)',
          defective: 'rgba(217, 176, 30, 1)',
          normal: 'rgba(48, 117, 221, 1)',
        },
      },
      siteCard: {
        bg: 'rgba(255, 255, 255, 1)',
        titleTextColor: 'rgba(0, 0, 0, 0.8)',
        siteIconColor: 'rgba(0, 0, 0, 0.8)',
        separatorColor: 'rgba(0, 0, 0, 0.1)',
        labelTextColor: 'rgba(0, 0, 0, 0.6)',
        valueTextColor: 'rgba(0, 0, 0, 0.8)',
      },
    },
    construction: {
      sectionTitleTextColor: 'rgba(0, 0, 0, 0.9)',
      sectionCounterTextColor: 'rgba(0, 0, 0, 0.15)',
      unscheduledJob: {
        titleTextColor: 'rgba(0, 0, 0, 0.5)',
        labelTextColor: 'rgba(0, 0, 0, 0.2)',
        valueTextColor: 'rgba(0, 0, 0, 0.6)',
        valueRangeTextColor: 'rgba(0, 0, 0, 0.3)',
      },
      jobDetailPage: {
        media: {
          titleTextColor: 'rgba(0, 0, 0, 0.8)',
        },
      },
    },
    siteDetail: {
      titleTextColor: 'rgba(0, 0, 0, 0.9)',
      bgColor: 'rgba(255, 255, 255, 0.9)',
      separatorColor: 'rgba(0, 0, 0, 0.15)',
      media: {
        counterOverlayBgColor: 'rgba(29, 34, 46, 0.5)',
        counterOverlayTextColor: 'rgba(255, 255, 255, 0.15)',
        placeholderBgColor: 'rgba(0, 0, 0, 0.4)',
        placeholderIconColor: 'rgba(255, 255, 255, 0.3)',
        titleBgColor: 'rgba(13, 13, 13, 0.42)',
        titleTextColor: 'rgba(255, 255, 255, 0.8)',
      },
      item: {
        labelTextColor: 'rgba(0, 0, 0, 0.3)',
        valueTextColor: 'rgba(0, 0, 0, 0.9)',
        valueTipTextColor: 'rgba(0, 0, 0, 0.5)',
        valueRangeTextColor: 'rgba(0, 0, 0, 0.6)',
        valueRangeLabelTextColor: 'rgba(0, 0, 0, 0.3)',
        descTextColor: 'rgba(0, 0, 0, 0.4)',
      },
      advice: {
        labelTextColor: 'rgba(0, 0, 0, 0.9)',
        serviceItem: {
          bgColor: 'rgba(48, 117, 221, 1)',
          textColor: 'rgba(255, 255, 255, 1)',
        },
      },
    },
    maintenancePlan: {
      iconColor: 'rgba(0, 0, 0, 0.8)',
      iconBgColor: 'rgba(0, 0, 0, 0.15)',
      defaultDefectiveBorderColor: 'rgba(48, 117, 221, 1)',
      viewDetailTextColor: 'rgba(0, 0, 0, 0.45)',
      confirmedLabelTextColor: '#19CAB5',
      slider: {
        thumbStartColor: 'rgba(48, 117, 221, 1)',
        thumbStopColor: 'rgba(45, 204, 225, 1)',
        thumbStartColorPending: '#FF9900',
        thumbStopColorPending: '#F2D671',
        thumbStartColorConfirmed: '#40C779',
        thumbStopColorConfirmed: '#19CAB5',
        thumbMileageTextColor: 'rgba(255, 255, 255, 1)',
        thumbTimeTextColor: 'rgba(255, 255, 255, 1)',
        thumbStatusTextColor: 'rgba(255, 255, 255, 0.75)',
        trackBgColor: 'rgba(242, 242, 242, 1)',
        tipTextColor: 'rgba(0, 0, 0, 0.15)',
        tickTextColor: 'rgba(0, 0, 0, 0.15)',
        tickColor: 'rgba(0, 0, 0, 0.05)',
      },
      custom: {
        tabTextColor: 'rgba(0, 0, 0, 0.4)',
        activeTabTextColor: 'rgba(0, 0, 0, 0.9)',
        tabUnderlineColor: 'rgba(48, 117, 221, 1)',
        textInputBgColor: 'rgba(255, 255, 255, 1)',
        textInputBorderColor: 'rgba(0, 0, 0, 0.3)',
        textInputUnitTextColor: 'rgba(0, 0, 0, 0.5)',
        textInputTextColor: 'rgba(0, 0, 0, 0.9)',
        pickerItemTextColor: 'rgba(0, 0, 0, 0.3)',
        activePickerItemTextColor: 'rgba(0, 0, 0, 0.9)',
      },
    },
    subscribeBindModal: {
      qrcode: {
        borderColor: 'rgba(0, 0, 0, 0.3)',
      },
    },
    medias: {
      section: {
        iconColor: 'rgba(0, 0, 0, 0.3)',
        titleColor: 'rgba(0, 0, 0, 0.6)',
        numberColor: 'rgba(0, 0, 0, 0.15)',
      },
    },
    timeline: {
      connectorLineColor: 'rgba(0, 0, 0, 0.1)',
      titleTextColor: 'rgba(0, 0, 0, 0.9)',
      taglineTextColor: 'rgba(0, 0, 0, 0.7)',
      authorTextColor: 'rgba(0, 0, 0, 0.4)',
      timestampTextColor: 'rgba(0, 0, 0, 0.4)',
    },
  },
  debugPanel: {
    sectionTitleTextColor: 'rgba(0, 0, 0, 0.4)',
    logSeparatorColor: 'rgba(0, 0, 0, 0.15)',
    logTextColor: 'rgba(0, 0, 0, 0.6)',
    logErrorTextColor: 'rgba(244, 79, 99, 1)',
    logActionTextColor: 'rgba(48, 117, 221, 1)',
  },
  storeInfoScreen: {
    logoImageBorderColor: 'rgba(230, 230, 230, 1)',
    storeNameTextColor: 'rgba(0, 0, 0, 1)',
    orgNameTextColor: 'rgba(0, 0, 0, 0.5)',
    descriptionTextColor: 'rgba(0, 0, 0, 0.5)',
    imagePlaceholderColor: 'rgba(0, 0, 0, 0.1)',
  },
  about: {
    nameTextColor: 'rgba(0, 0, 0, 1)',
    infoLabelTextColor: 'rgba(0, 0, 0, 0.35)',
    infoTextColor: 'rgba(0, 0, 0, 0.85)',
    separatorLineColor: 'rgba(0, 0, 0, 0.05)',
    linkTextColor: 'rgba(48, 117, 221, 1)',
    descriptionTextColor: 'rgba(0, 0, 0, 0.5)',
    secondaryLinkTextColor: 'rgba(0, 0, 0, 0.4)',
    copyrightTextColor: 'rgba(0, 0, 0, 0.4)',
  },
};

export default theme;
